import { Fragment, useState } from "react";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import useIsTaboolaEnabled from "@features/taboola/hooks/useIsTaboolaEnabled";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import ArticleDivider from "@routes/Article/components/ArticleDivider";
import LayoutFactory from "@routes/Article/components/Layout/LayoutFactory";
import { useNextArticle } from "@routes/Article/hooks/useNextArticle";
import {
  getUrlPathFromAbsoluteUrl,
  VerticalHeaderFactory,
} from "@routes/Article/utils/helpers";
import { ArticlePageData, FeedArticleItems } from "@routes/Article/utils/types";
import { ArticleDisplayType } from "@typings/enums";

const ArticleContentAppWebView = ({
  articleData,
  isPreview,
}: ArticlePageData): React.ReactElement => {
  const [articleInView, setArticleInView] =
    useState<FeedArticleItems>(articleData);

  // Article data de-construct
  const { sections, paidMode, sentiment } = articleData;

  const { moreStories } = useNextArticle({
    data: articleData,
  });

  const firstArticleUrlPath =
    getUrlPathFromAbsoluteUrl(moreStories[0]?.urlPath) || "";

  const { gsChannels } = useGrapeshotData({
    urlPath: firstArticleUrlPath,
    index: -1,
    keywords: sentiment?.keywords,
  });

  const isTaboolaEnabled = useIsTaboolaEnabled();

  const mainSection = sections?.[0];
  const uniqueName = mainSection?.uniqueName || "";
  const uniqueNameAds = uniqueName?.replace("_", "/") || "";
  const verticalPath = "/" + uniqueNameAds;

  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const handleSetArticle = (e: FeedArticleItems) => {
    setArticleInView(e);
  };

  const { isGiftReceived } = useArticleGiftReceived({ paidMode });

  const isPremium = paidMode === "premium";

  const {
    alacrityRes: giftURlAlacrityRes,
    isModalOpen: giftURlisModalOpen,
    setIsModalOpen: setGiftURLIsModalOpen,
    handleGenerateGiftURLModal: handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);

  const articleInViewGiftRes = giftURlAlacrityRes.find(
    (res) => res.pathname === articleInView.urlPath
  );

  return (
    <div className="w-full" data-testid="article-content-component">
      <>
        {VerticalHeaderLayout ? (
          <Container rootClassName="py-2">
            <VerticalHeaderLayout />
          </Container>
        ) : null}

        <div id="article-content-wrapper" className="w-full">
          {moreStories.map((article, index) => {
            // Check if article is undefined
            if (!article) return null;

            const displayType =
              article?.displayType || ArticleDisplayType.MainNewsDefault;

            const ArticleLayout = LayoutFactory({
              displayType,
              path: article.urlPath,
            });

            if (!ArticleLayout) return null;

            return (
              <Fragment key={article.id}>
                {index > 0 ? <ArticleDivider /> : null}

                <ArticleLayout
                  article={article}
                  index={index}
                  gsChannels={gsChannels}
                  handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                  setIsModalOpen={setGiftURLIsModalOpen}
                  alacrityRes={articleInViewGiftRes?.alacrityRes}
                  isPremium={isPremium}
                  isModalOpen={giftURlisModalOpen}
                  setArticleObj={handleSetArticle}
                  articleInView={articleInView}
                  isGifted={isGiftReceived}
                  isPreview={isPreview}
                  isTaboolaEnabled={isTaboolaEnabled}
                />
              </Fragment>
            );
          })}
        </div>

        <Ad adType="catfish" />
        <Ad adType="prestitial" />
      </>
    </div>
  );
};

export default ArticleContentAppWebView;
